import { PageSize } from '@/constant';
import {
	GetCommonInquiryBoardListParams,
	ICommonInquiryBoard,
	TPayloadUpdateSuccess,
} from '@/services';
import { mergeItemUpdated } from '@/utils';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { RootState } from '..';
import {
	ICommonInquiryBoardDetailStore,
	ICreateInquiryBoardRequest,
	IPayloadDeleteCommonInquiryBoardRequest,
	IPayloadGetCommonInquiryBoardDetailAnswerSuccess,
	IPayloadGetCommonInquiryBoardDetailRequest,
	IPayloadGetCommonInquiryBoardDetailSuccess,
	IPayloadGetCommonInquiryBoardListRequest,
	IPayloadGetCommonInquiryBoardListSuccess,
} from './common-inquiry-board.type';
const InitParams: GetCommonInquiryBoardListParams = {
	page: 1,
	pageSize: PageSize.S,
	searchTypes: [],
};

const initialState = {
	fetching: false,
	fetchingAnswerDetail: false,
	list: [] as ICommonInquiryBoardDetailStore[],
	total: 0,
	detail: {} as ICommonInquiryBoardDetailStore,
	answerDetail: {} as ICommonInquiryBoardDetailStore,
	params: InitParams,
};

const commonInquiryBoardSlice = createSlice({
	initialState,
	name: 'commonInquiryBoard',
	reducers: {
		/**---- GET COMMON INQUIRY BOARD LIST START ---- */
		getCommonInquiryBoardListRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardListRequest>
		) => {
			state.fetching = true;
			state.params = action?.payload;
		},
		getCommonInquiryBoardListSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardListSuccess>
		) => {
			const { total, content } = action.payload;
			state.fetching = false;
			state.list = content;
			state.total = total;
		},
		getCommonInquiryBoardListFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMMON INQUIRY BOARD LIST END ---- */

		/**---- GET COMMON INQUIRY BOARD DETAIL START ---- */
		getCommonInquiryBoardDetailRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardDetailRequest>
		) => {
			state.fetching = true;
		},
		getCommonInquiryBoardDetailSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardDetailSuccess>
		) => {
			const { detail } = action.payload;
			state.fetching = false;
			state.detail = detail;
		},
		getCommonInquiryBoardDetailFailure: (state) => {
			state.fetching = false;
		},
		/**---- GET COMMON INQUIRY BOARD DETAIL END ---- */

		/**---- GET COMMON INQUIRY BOARD DETAIL ANSWER START ---- */
		getCommonInquiryBoardDetailAnswerRequest: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardDetailRequest>
		) => {
			state.fetchingAnswerDetail = true;
		},
		getCommonInquiryBoardDetailAnswerSuccess: (
			state,
			action: PayloadAction<IPayloadGetCommonInquiryBoardDetailAnswerSuccess>
		) => {
			const { detail } = action.payload;
			state.fetchingAnswerDetail = false;
			state.answerDetail = detail;
		},
		getCommonInquiryBoardDetailAnswerFailure: (state) => {
			state.fetchingAnswerDetail = false;
		},
		/**---- GET COMMON INQUIRY BOARD DETAIL ANSWER END ---- */

		/**---- CREATE COMMON INQUIRY BOARD START ---- */
		createCommonInquiryBoardRequest: (
			state,
			action: PayloadAction<ICreateInquiryBoardRequest>
		) => {
			state.fetching = true;
		},
		createCommonInquiryBoardSuccess: (state) => {
			state.fetching = false;
		},
		createCommonInquiryBoardFailure: (state) => {
			state.fetching = false;
		},
		/**---- CREATE COMMON INQUIRY BOARD END ---- */

		/**---- DELETE COMMON INQUIRY BOARD START ---- */
		deleteCommonInquiryBoardRequest: (
			state,
			action: PayloadAction<IPayloadDeleteCommonInquiryBoardRequest>
		) => {
			state.fetching = true;
		},
		deleteCommonInquiryBoardSuccess: (state) => {
			state.fetching = false;
		},
		deleteCommonInquiryBoardFailure: (state) => {
			state.fetching = false;
		},
		/**---- DELETE COMMON INQUIRY BOARD END ---- */

		/**---- UPDATE COMMON INQUIRY BOARD START ---- */
		updateCommonInquiryBoardDetailSuccess: (
			state,
			action: PayloadAction<TPayloadUpdateSuccess<ICommonInquiryBoard>>
		) => {
			const { data } = action.payload;
			const detail = { ...state.detail };
			state.detail = mergeItemUpdated<
				ICommonInquiryBoardDetailStore,
				ICommonInquiryBoard
			>(detail, data);
		},
		/**---- UPDATE COMMON INQUIRY BOARD END ---- */
	},
});

export const CommonInquiryBoardSelectors = {
	selectFetching: (state: RootState) => state.commonInquiryBoard.fetching,
	selectFetchingAnswerDetail: (state: RootState) =>
		state.commonInquiryBoard.fetchingAnswerDetail,
	selectTotal: (state: RootState) => state.commonInquiryBoard.total,
	selectList: (state: RootState) => state.commonInquiryBoard.list,
	selectDetail: (state: RootState) => state.commonInquiryBoard.detail,
	selectAnswerDetail: (state: RootState) =>
		state.commonInquiryBoard.answerDetail,
	selectParams: (state: RootState) => state.commonInquiryBoard.params,
};

export const CommonInquiryBoardActions = commonInquiryBoardSlice.actions;
export const CommonInquiryBoardReducer = commonInquiryBoardSlice.reducer;
